import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Button, Card, CardContent, 
  CardActions, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, Checkbox,
  FormControlLabel, List, ListItem, ListItemText, 
  ListItemAvatar, Avatar, Divider, Box, CircularProgress,
  Grid, Chip, FormControl, RadioGroup, Radio, FormLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { normalizeImageUrl } from '../components/Post';
import './Lists.css';
import { apiService } from '../utils/api';
import config from '../config';

const Lists = () => {
  const { token, currentUser, checkPermission } = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = !!currentUser; // currentUserの有無でログイン状態を判断

  const [userLists, setUserLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // 新規リスト作成用の状態
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  
  // リスト編集用の状態
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsPrivate, setEditIsPrivate] = useState(true);
  
  // 公開リスト作成権限
  const [canCreatePublicList, setCanCreatePublicList] = useState(false);
  
  // リストメンバー追加用の状態
  const [followingUsers, setFollowingUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectMemberDialog, setSelectMemberDialog] = useState(false);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  
  // リストの詳細情報取得
  const [selectedListDetails, setSelectedListDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // 初期化時にリスト一覧を取得
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserLists();
      // 公開リスト作成権限をチェック
      const hasPermission = checkPermission('CREATE_PUBLIC_LIST');
      setCanCreatePublicList(hasPermission);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, checkPermission]);

  // 自分のリスト取得API呼び出しを修正
  const fetchUserLists = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/lists', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          _t: Date.now() // キャッシュ回避のためのタイムスタンプ
        }
      });
      
      setUserLists(response.data.lists || []);
      setLoading(false); // ローディング状態を必ず解除
    } catch (error) {
      
      setLoading(false);
    }
  };

  // フォロー中のユーザー一覧を取得
  const fetchFollowingUsers = async () => {
    try {
      const response = await apiService.get('/api/lists/following', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          _t: Date.now() // キャッシュ回避のためのタイムスタンプ
        }
      });
      setFollowingUsers(response.data.users || []);
    } catch (error) {
      
    }
  };

  // リスト詳細を取得
  const fetchListDetails = async (listId) => {
    try {
      setLoadingDetails(true);
      const response = await apiService.get(`/api/lists/${listId}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          _t: Date.now() // キャッシュ回避のためのタイムスタンプ
        }
      });
      setSelectedListDetails(response.data);
      setLoadingDetails(false); // ローディング状態を成功時にも解除
    } catch (error) {
      
      setLoadingDetails(false);
    }
  };

  // リスト選択時の処理
  const handleListSelect = (list) => {
    setSelectedList(list);
    fetchListDetails(list.id);
  };

  // 新規リスト作成ダイアログを開く
  const handleOpenDialog = () => {
    setOpenDialog(true);
    fetchFollowingUsers();
    setSelectedUsers([]);
    setIsPrivate(true); // デフォルトは非公開
  };

  // リスト作成ダイアログを閉じる
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setListName('');
    setListDescription('');
    setSelectedUsers([]);
    setIsPrivate(true);
  };

  // リスト編集ダイアログを開く
  const handleOpenEditDialog = (list) => {
    setSelectedList(list);
    setEditName(list.name);
    setEditDescription(list.description || '');
    setEditIsPrivate(list.is_private);
    setOpenEditDialog(true);
  };

  // リスト編集ダイアログを閉じる
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  // リスト削除確認ダイアログを開く
  const handleOpenDeleteDialog = (list) => {
    setSelectedList(list);
    setDeleteConfirmDialog(true);
  };

  // リスト削除確認ダイアログを閉じる
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog(false);
  };

  // メンバー選択ダイアログを開く
  const handleOpenMemberDialog = () => {
    fetchFollowingUsers();
    setSelectMemberDialog(true);
  };

  // メンバー選択ダイアログを閉じる
  const handleCloseMemberDialog = () => {
    setSelectMemberDialog(false);
  };

  // メンバー表示ダイアログを開く
  const handleOpenMembersDialog = (list) => {
    setSelectedList(list);
    fetchListDetails(list.id);
    setShowMembersDialog(true);
  };

  // メンバー表示ダイアログを閉じる
  const handleCloseMembersDialog = () => {
    setShowMembersDialog(false);
  };

  // ユーザー選択の切り替え
  const handleUserToggle = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // リストを作成
  const handleCreateList = async () => {
    if (!listName.trim()) {
      alert('リスト名を入力してください');
      return;
    }

    try {
      const response = await apiService.post('/api/lists', {
        name: listName,
        description: listDescription,
        is_private: isPrivate,
        member_ids: selectedUsers
      });

      // リスト一覧を更新
      fetchUserLists();
      handleCloseDialog();
    } catch (error) {
      
      if (error.response && error.response.data && error.response.data.error) {
        alert(`リストの作成に失敗しました: ${error.response.data.error}`);
      } else {
        alert('リストの作成に失敗しました');
      }
    }
  };

  // リストを更新
  const handleUpdateList = async () => {
    if (!editName.trim()) {
      alert('リスト名を入力してください');
      return;
    }

    try {
      const response = await apiService.put(`/api/lists/${selectedList.id}`, {
        name: editName,
        description: editDescription,
        is_private: editIsPrivate
      });

      // リスト一覧を更新
      fetchUserLists();
      
      // 選択中のリストも更新
      if (selectedList) {
        setSelectedList(response.data);
        
        // リスト詳細を更新
        if (selectedListDetails) {
          setSelectedListDetails(response.data);
        }
      }
      
      handleCloseEditDialog();
    } catch (error) {
      
      if (error.response && error.response.data && error.response.data.error) {
        alert(`リストの更新に失敗しました: ${error.response.data.error}`);
      } else {
        alert('リストの更新に失敗しました');
      }
    }
  };

  // リストを削除
  const handleDeleteList = async () => {
    try {
      await apiService.delete(`/api/lists/${selectedList.id}`);

      // リスト一覧を更新
      fetchUserLists();
      
      // 選択中のリストをクリア
      if (selectedList && selectedList.id === selectedList.id) {
        setSelectedList(null);
        setSelectedListDetails(null);
      }
      
      handleCloseDeleteDialog();
    } catch (error) {
      
      alert('リストの削除に失敗しました');
    }
  };

  // リストにメンバーを追加
  const handleAddMember = async (userId) => {
    if (!selectedList) return;
    
    try {
      setAddMemberLoading(true);
      const response = await apiService.post(`/api/lists/${selectedList.id}/members`, {
        member_id: userId
      });

      // リスト詳細を更新
      fetchListDetails(selectedList.id);
      setAddMemberLoading(false);
    } catch (error) {
      
      setAddMemberLoading(false);
      alert('メンバーの追加に失敗しました');
    }
  };

  // リストからメンバーを削除
  const handleRemoveMember = async (memberId) => {
    if (!selectedList) return;
    
    try {
      await apiService.delete(`/api/lists/${selectedList.id}/members/${memberId}`);

      // リスト詳細を更新
      fetchListDetails(selectedList.id);
    } catch (error) {
      
      alert('メンバーの削除に失敗しました');
    }
  };

  // フォロー中のユーザーでリストにまだ追加されていないユーザーをフィルタリング
  const getAvailableUsers = () => {
    if (!selectedListDetails || !followingUsers || !selectedListDetails.members) return [];
    
    const currentMemberIds = selectedListDetails.members.map(member => member.id);
    return followingUsers.filter(user => !currentMemberIds.includes(user.id));
  };

  // リストアイコンを表示（公開/非公開）
  const getListIcon = (isPrivate) => {
    return isPrivate ? 
      <LockIcon fontSize="small" style={{ marginRight: 8 }} /> : 
      <PublicIcon fontSize="small" style={{ marginRight: 8, color: '#1976d2' }} />;
  };

  return (
    <Container>
      <Box mb={3} mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" component="h1">リスト</Typography>
        {isAuthenticated && (
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            新規リスト作成
          </Button>
        )}
      </Box>
      
      {!isAuthenticated ? (
        <Card className="lists__loginCard">
          <CardContent>
            <Typography variant="h6">リスト機能を利用するにはログインが必要です</Typography>
            <Typography variant="body2" color="textSecondary">
              ログインすると、お気に入りのユーザーをリストにまとめて整理することができます。
            </Typography>
          </CardContent>
          <CardActions>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/login')}
            >
              ログイン
            </Button>
            <Button 
              variant="outlined"
              onClick={() => navigate('/register')}
            >
              アカウント作成
            </Button>
          </CardActions>
        </Card>
      ) : loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card className="lists__listsCard">
              <CardContent>
                <Typography variant="h6" gutterBottom>マイリスト</Typography>
                {userLists.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    リストがまだありません。「新規リスト作成」ボタンからリストを作成しましょう。
                  </Typography>
                ) : (
                  <List>
                    {userLists.map((list) => (
                      <React.Fragment key={list.id}>
                        <ListItem 
                          button 
                          selected={selectedList && selectedList.id === list.id}
                          onClick={() => handleListSelect(list)}
                          className="lists__listItem"
                        >
                          <ListItemText 
                            primary={
                              <Box display="flex" alignItems="center">
                                {getListIcon(list.is_private)}
                                {list.name}
                              </Box>
                            }
                            secondary={`${list.members_count}人のメンバー`} 
                          />
                          <Box>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMembersDialog(list);
                              }}
                            >
                              <PeopleIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(list);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteDialog(list);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={8}>
            {selectedList && selectedListDetails ? (
              <Card className="lists__detailCard">
                {loadingDetails ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h5">
                          {getListIcon(selectedListDetails.is_private)}
                          {selectedListDetails.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {selectedListDetails.members_count}人のメンバー
                        </Typography>
                      </Box>
                      <Button 
                        variant="outlined" 
                        startIcon={<PersonAddIcon />}
                        onClick={handleOpenMemberDialog}
                      >
                        メンバーを追加
                      </Button>
                    </Box>
                    
                    {selectedListDetails.description && (
                      <Typography variant="body1" paragraph>
                        {selectedListDetails.description}
                      </Typography>
                    )}
                    
                    <Box mt={1} mb={2}>
                      <Chip 
                        icon={getListIcon(selectedListDetails.is_private)} 
                        label={selectedListDetails.is_private ? "非公開リスト" : "公開リスト"} 
                        variant="outlined" 
                        color={selectedListDetails.is_private ? "default" : "primary"}
                      />
                    </Box>
                    
                    <Divider style={{ margin: '16px 0' }} />
                    
                    <Typography variant="h6" gutterBottom>メンバー</Typography>
                    
                    {!selectedListDetails.members || selectedListDetails.members.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        まだメンバーがいません。フォローしているユーザーをリストに追加しましょう。
                      </Typography>
                    ) : (
                      <List>
                        {selectedListDetails.members.map((member) => (
                          <React.Fragment key={member.id}>
                            <ListItem 
                              className="lists__memberItem"
                              button
                              onClick={() => navigate(`/profile/${member.username}`)}
                            >
                              <ListItemAvatar>
                                <Avatar 
                                  src={normalizeImageUrl(member.avatar) || '/images/default-avatar.png'} 
                                  alt={member.display_name || member.username} 
                                />
                              </ListItemAvatar>
                              <ListItemText 
                                primary={member.display_name || member.username} 
                                secondary={`@${member.username}`} 
                              />
                              <IconButton 
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveMember(member.id);
                                }}
                              >
                                <PersonRemoveIcon />
                              </IconButton>
                            </ListItem>
                            <Divider component="li" />
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </CardContent>
                )}
              </Card>
            ) : (
              <Card className="lists__emptyCard">
                <CardContent>
                  <Typography variant="h6" align="center" gutterBottom>
                    リストを選択してください
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    左側のリスト一覧からリストを選択するか、新しいリストを作成してください。
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}
      
      {/* 新規リスト作成ダイアログ */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>新規リスト作成</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="リスト名"
            type="text"
            fullWidth
            variant="outlined"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            required
            inputProps={{ maxLength: 50 }}
            helperText={`${listName.length}/50`}
          />
          <TextField
            margin="dense"
            label="説明（任意）"
            type="text"
            fullWidth
            variant="outlined"
            value={listDescription}
            onChange={(e) => setListDescription(e.target.value)}
            multiline
            rows={3}
            inputProps={{ maxLength: 280 }}
            helperText={`${listDescription.length}/280`}
          />
          
          {canCreatePublicList && (
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">プライバシー設定</FormLabel>
              <RadioGroup
                value={isPrivate}
                onChange={(e) => setIsPrivate(e.target.value === 'true')}
              >
                <FormControlLabel 
                  value={true} 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center">
                      <LockIcon fontSize="small" sx={{ mr: 1 }}/>
                      <span>非公開リスト（自分だけが閲覧可能）</span>
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value={false} 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center">
                      <PublicIcon fontSize="small" sx={{ mr: 1 }}/>
                      <span>公開リスト（プロフィールから閲覧可能）</span>
                    </Box>
                  } 
                />
              </RadioGroup>
            </FormControl>
          )}
          
          {!canCreatePublicList && (
            <Box mt={2} display="flex" alignItems="center">
              <LockIcon fontSize="small" style={{ marginRight: 8 }} />
              <Typography variant="body2" color="textSecondary">
                このリストは自分だけが閲覧できます
              </Typography>
            </Box>
          )}
          
          <Typography variant="subtitle1" style={{ marginTop: 16 }}>
            メンバーを選択（フォロー中のユーザー）
          </Typography>
          
          {followingUsers.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              フォロー中のユーザーがいません。ユーザーをフォローするとリストに追加できます。
            </Typography>
          ) : (
            <List className="lists__selectUsersList">
              {followingUsers.map((user) => (
                <React.Fragment key={user.id}>
                  <ListItem 
                    button 
                    onClick={() => handleUserToggle(user.id)}
                    selected={selectedUsers.includes(user.id)}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={normalizeImageUrl(user.avatar) || '/images/default-avatar.png'} 
                        alt={user.display_name || user.username} 
                      />
                    </ListItemAvatar>
                    <ListItemText 
                      primary={user.display_name || user.username} 
                      secondary={`@${user.username}`} 
                    />
                    <Checkbox
                      edge="end"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => {}}
                      inputProps={{ 'aria-labelledby': `checkbox-${user.id}` }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button 
            onClick={handleCreateList} 
            color="primary"
            variant="contained"
            disabled={!listName.trim()}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* リスト編集ダイアログ */}
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>リストを編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="リスト名"
            type="text"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            required
            inputProps={{ maxLength: 50 }}
            helperText={`${editName.length}/50`}
          />
          <TextField
            margin="dense"
            label="説明（任意）"
            type="text"
            fullWidth
            variant="outlined"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            multiline
            rows={3}
            inputProps={{ maxLength: 280 }}
            helperText={`${editDescription.length}/280`}
          />
          
          {canCreatePublicList && (
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">プライバシー設定</FormLabel>
              <RadioGroup
                value={editIsPrivate}
                onChange={(e) => setEditIsPrivate(e.target.value === 'true')}
              >
                <FormControlLabel 
                  value={true} 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center">
                      <LockIcon fontSize="small" sx={{ mr: 1 }}/>
                      <span>非公開リスト（自分だけが閲覧可能）</span>
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value={false} 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center">
                      <PublicIcon fontSize="small" sx={{ mr: 1 }}/>
                      <span>公開リスト（プロフィールから閲覧可能）</span>
                    </Box>
                  } 
                />
              </RadioGroup>
            </FormControl>
          )}
          
          {!canCreatePublicList && (
            <Box mt={2} display="flex" alignItems="center">
              <LockIcon fontSize="small" style={{ marginRight: 8 }} />
              <Typography variant="body2" color="textSecondary">
                このリストは自分だけが閲覧できます
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>キャンセル</Button>
          <Button 
            onClick={handleUpdateList} 
            color="primary"
            variant="contained"
            disabled={!editName.trim()}
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* リスト削除確認ダイアログ */}
      <Dialog 
        open={deleteConfirmDialog} 
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>リストを削除</DialogTitle>
        <DialogContent>
          <Typography>
            リスト「{selectedList?.name}」を削除しますか？この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button 
            onClick={handleDeleteList} 
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* メンバー追加ダイアログ */}
      <Dialog 
        open={selectMemberDialog} 
        onClose={handleCloseMemberDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>メンバーを追加</DialogTitle>
        <DialogContent>
          {addMemberLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                フォロー中のユーザー（リストに未追加）
              </Typography>
              
              {getAvailableUsers().length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  追加できるユーザーがいません。新しいユーザーをフォローするか、すでに全てのフォロー中ユーザーがリストに追加されています。
                </Typography>
              ) : (
                <List className="lists__selectUsersList">
                  {getAvailableUsers().map((user) => (
                    <React.Fragment key={user.id}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar 
                            src={normalizeImageUrl(user.avatar) || '/images/default-avatar.png'} 
                            alt={user.display_name || user.username} 
                          />
                        </ListItemAvatar>
                        <ListItemText 
                          primary={user.display_name || user.username} 
                          secondary={`@${user.username}`} 
                        />
                        <Button 
                          variant="outlined"
                          startIcon={<PersonAddIcon />}
                          onClick={() => handleAddMember(user.id)}
                        >
                          追加
                        </Button>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMemberDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>
      
      {/* メンバー表示ダイアログ */}
      <Dialog 
        open={showMembersDialog} 
        onClose={handleCloseMembersDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedList?.name} のメンバー
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {(!selectedListDetails || !selectedListDetails.members || selectedListDetails.members.length === 0) ? (
                <Typography variant="body2" color="textSecondary">
                  メンバーがいません
                </Typography>
              ) : (
                <List>
                  {selectedListDetails.members.map((member) => (
                    <React.Fragment key={member.id}>
                      <ListItem>
                                                  <ListItemAvatar>
                            <Avatar 
                              src={normalizeImageUrl(member.avatar) || '/images/default-avatar.png'} 
                              alt={member.display_name || member.username} 
                            />
                        </ListItemAvatar>
                        <ListItemText 
                          primary={member.display_name || member.username} 
                          secondary={`@${member.username}`} 
                        />
                        <IconButton 
                          color="error"
                          onClick={() => handleRemoveMember(member.id)}
                        >
                          <PersonRemoveIcon />
                        </IconButton>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            startIcon={<PersonAddIcon />}
            onClick={() => {
              handleCloseMembersDialog();
              handleOpenMemberDialog();
            }}
          >
            メンバーを追加
          </Button>
          <Button onClick={handleCloseMembersDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Lists;

 